import {lazy, FC, Suspense, ReactNode} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../template/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {AppsWrapper} from '../pages/apps/AppsWrapper'
import {getCSSVariableValue} from '../template/assets/ts/_utils'
import {usersAPI} from '../services/UsersService'
import AppsPage from '../modules/apps/AppsPage'
import {CDRWrapper} from '../pages/cdr/CDRWrapper'
import {CSRWrapper} from '../pages/csr/CSRWrapper'
import CDRPage from '../modules/cdr/CDRPage'
import CSRModule from '../modules/csr/CSRModule'
import {AccountUsersPage} from '../modules/account/users/AccountUsersPage'
import {useAccount} from '../hooks/useAccount'

const PrivateRoutes = () => {
  const {data: currentUser} = usersAPI.useFetchGetMeQuery()
  const {isAccount, role, accounts} = useAccount()
  const canManageAccountUsers = (isAccount && role === 'OWNER') || (role === 'SUPERVISOR' && accounts.length > 1)
  const ManagementPage = lazy(() => import('../modules/management/ManagementPage'))
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path='auth/*' element={<Navigate to='/apps/' />} />
        {currentUser?.role === 'ADMIN' && (
          <Route
            path='management/*'
            element={
              <SuspensedView>
                <ManagementPage />
              </SuspensedView>
            }
          />
        )}
        {canManageAccountUsers && (
          <Route
            path='account/users/'
            element={
              <SuspensedView>
                <AccountUsersPage />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='apps/'
          element={
            <SuspensedView>
              <AppsWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='apps/:appId/*'
          element={
            <SuspensedView>
              <AppsPage />
            </SuspensedView>
          }
        />
        <Route
          path='cdr/'
          element={
            <SuspensedView>
              <CDRWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='cdr/:appId/*'
          element={
            <SuspensedView>
              <CDRPage />
            </SuspensedView>
          }
        />
        <Route
          path='csr/'
          element={
            <SuspensedView>
              <CSRWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='csr/:appId/*'
          element={
            <SuspensedView>
              <CSRModule />
            </SuspensedView>
          }
        />
        <Route
          path='settings/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route path='*' element={<Navigate to='/error/404/' />} />
        <Route path='account/*' element={<Navigate to='/apps/' replace />} />
      </Route>
    </Routes>
  )
}

type WithChildren = {
  children?: ReactNode
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
